import {
  Activity,
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  BadgeCheck,
  Barcode,
  Bell,
  Boxes,
  Briefcase,
  Building2,
  Calendar,
  CalendarDays,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleCheck,
  CircleCheckBig,
  CircleDashed,
  CircleQuestionMark,
  CircleX,
  ClipboardList,
  Clock,
  Cpu,
  CreditCard,
  Download,
  Eye,
  EyeOff,
  Factory,
  FileArchive,
  FileClock,
  FileSpreadsheet,
  FileText,
  Funnel,
  GitBranch,
  HardDriveDownload,
  Inbox,
  Info,
  KeyRound,
  Layers,
  LayoutDashboard,
  LayoutList,
  ListChecks,
  Loader,
  LockKeyhole,
  LockOpen,
  LogOut,
  Mail,
  MapPin,
  Megaphone,
  Monitor,
  Network,
  NotebookPen,
  OctagonAlert,
  PackageCheck,
  PackageX,
  PanelLeftClose,
  PanelLeftOpen,
  Paperclip,
  Pencil,
  PencilLine,
  Plus,
  Printer,
  RefreshCw,
  Rocket,
  RotateCcw,
  Route,
  Save,
  ScanBarcode,
  ScrollText,
  Search,
  SendHorizontal,
  Shield,
  ShieldCheck,
  Shuffle,
  SlidersHorizontal,
  Sparkles,
  SquarePen,
  Store,
  ShoppingBag,
  Warehouse,
  Landmark,
  Tag,
  Trash2,
  TriangleAlert,
  Truck,
  Upload,
  User,
  UserRound,
  UserRoundCog,
  Users,
  WifiOff,
  Wrench,
  X,
} from 'lucide';

/** lucide IconNode 형식: readonly [tag, attrs][] (lucide 1.x — SVGProps는 undefined 허용) */
export type PkIconData = readonly (readonly [string, Record<string, string | number | undefined>])[];

/**
 * 공용 아이콘 레지스트리 — 쓸 아이콘만 등록한다 (트리셰이킹 유지).
 * 새 아이콘이 필요하면 lucide에서 import해 여기에 추가하고 쇼케이스(/styleguide)에서 확인한다.
 * 키는 용도가 드러나는 이름을 우선한다 (공통 7버튼: search/plus/save/trash/excel/printer/reset/x).
 * 메뉴·화면 제목용 아이콘은 `shared/codes/menu-icons.ts`의 맵이 이 키를 가리킨다 — 사이드바·탭·
 * 브레드크럼·화면 제목이 같은 맵을 읽어 아이콘이 항상 일치한다.
 */
export const PK_ICONS = {
  // 공통 7버튼·폼 조작
  search: Search,
  plus: Plus,
  save: Save,
  trash: Trash2,
  excel: FileSpreadsheet,
  printer: Printer,
  reset: RotateCcw,
  x: X,
  calendar: Calendar,
  upload: Upload,
  download: Download,
  warning: TriangleAlert,
  success: CircleCheck,
  error: CircleX,
  info: Info,
  check: Check,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'arrow-right': ArrowRight,
  'arrow-left': ArrowLeft,
  pencil: Pencil,
  clock: Clock,
  eye: Eye,
  'eye-off': EyeOff,
  refresh: RefreshCw,
  file: FileText,
  attach: Paperclip,
  building: Building2,
  /** 계층 아이콘 — 메뉴 키를 빌려 쓰지 않는다(그룹 아이콘을 바꾸면 계층 아이콘이 따라 바뀌는 결합 방지, 리뷰 W1) */
  landmark: Landmark,
  warehouse: Warehouse,
  user: User,
  loader: Loader,
  'log-out': LogOut,
  'panel-left-close': PanelLeftClose,
  'panel-left-open': PanelLeftOpen,

  // 메뉴 (그룹 3 + 화면 13 + 화면 없는 권한 단위 3 + 폴백)
  'menu-org-group': Briefcase,
  'menu-product-group': Boxes,
  'menu-merchant-group': ShoppingBag, // 그룹은 리프(store)와 다른 글리프 — 사이드바에서 겹쳐 보이지 않게
  'menu-main': LayoutDashboard,
  'menu-notice': Megaphone,
  'menu-org': Building2,
  'menu-user': Users,
  'menu-page': LayoutList,
  'menu-model': Cpu,
  'menu-product': ScanBarcode,
  'menu-as': Wrench,
  'menu-merchant': Store,
  'menu-deploy': Rocket,
  'menu-stat': ChartColumn,
  'menu-comm-log': ArrowLeftRight, // 수신·송신 두 방향 — 로그가 전문 왕복 기록임을 드러낸다
  'menu-audit-log': ScrollText, // 두루마리 기록 — 사람이 남긴 작업 이력(감사 로그)
  'menu-serial': Barcode,
  'menu-firmware': HardDriveDownload,
  'menu-reader-setting': SlidersHorizontal,
  'menu-unknown': CircleDashed,

  // 셸 부속
  'user-round': UserRound,
  inbox: Inbox,
  'octagon-alert': OctagonAlert,
  'circle-question': CircleQuestionMark,

  // 메인 운영현황·공지
  'status-ok': CircleCheckBig,
  'status-fault': TriangleAlert,
  'status-off': WifiOff,
  network: Network,
  'notice-edit': NotebookPen,
  sparkles: Sparkles,
  bell: Bell,
  tag: Tag,

  // 업체·사용자·PAGE
  'git-branch': GitBranch,
  factory: Factory,
  /** VAN/PG사 계층 — 결제망 사업자 (제조사 factory와 짝을 이루는 계정 없는 참조 계층) */
  'credit-card': CreditCard,
  'user-cog': UserRoundCog,
  'shield-check': ShieldCheck,
  shield: Shield,
  lock: LockKeyhole,
  /** 비밀번호 찾기의 등록 이메일 입력 */
  mail: Mail,
  unlock: LockOpen,
  'key-round': KeyRound,
  'square-pen': SquarePen,

  // 모델·제품
  'file-clock': FileClock,
  'badge-check': BadgeCheck,
  'pencil-line': PencilLine,
  'check-big': CircleCheckBig,
  send: SendHorizontal,
  'calendar-days': CalendarDays,

  // A/S·이력
  monitor: Monitor,
  truck: Truck,
  'map-pin': MapPin,
  'package-x': PackageX,
  'package-check': PackageCheck,
  activity: Activity,
  route: Route,
  'clipboard-list': ClipboardList,
  'file-archive': FileArchive,

  // 가맹점·배포·통계
  shuffle: Shuffle,
  funnel: Funnel,
  'list-checks': ListChecks,
  layers: Layers,
} as const satisfies Record<string, PkIconData>;

export type PkIconName = keyof typeof PK_ICONS;

export const PK_ICON_NAMES = Object.keys(PK_ICONS) as PkIconName[];
